// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * S-expression tokenizer (spec §6.1).
 *
 * Handles `(`, `)`, bare atoms and double-quoted strings with `\"`, `\\`, `\n`
 * and `\t` escapes. Input is a JS string, so it is already UTF-8 decoded.
 */

export type TokenKind = 'open' | 'close' | 'atom' | 'string';

export interface Token {
  kind: TokenKind;
  /** Decoded value; empty for `open`/`close`. */
  value: string;
  /** Byte-free character offset into the source. */
  offset: number;
  line: number;
  column: number;
}

/** A parse or tokenize failure carrying a position, for §7 error reporting. */
export class SexpError extends Error {
  readonly offset: number;
  readonly line: number;
  readonly column: number;
  readonly fileName: string;

  constructor(
    message: string,
    pos: { offset: number; line: number; column: number },
    fileName = '<input>',
  ) {
    super(`${fileName}:${pos.line}:${pos.column}: ${message}`);
    this.name = 'SexpError';
    this.offset = pos.offset;
    this.line = pos.line;
    this.column = pos.column;
    this.fileName = fileName;
  }
}

// Character codes, compared directly: a `.kicad_pcb` at the §4 performance
// target is tens of megabytes, and this loop runs once per character.
const CH_TAB = 9;
const CH_LF = 10;
const CH_VT = 11;
const CH_FF = 12;
const CH_CR = 13;
const CH_SPACE = 32;
const CH_QUOTE = 34;
const CH_OPEN = 40;
const CH_CLOSE = 41;
const CH_BACKSLASH = 92;

function isWhitespace(c: number): boolean {
  return c === CH_SPACE || c === CH_TAB || c === CH_LF || c === CH_CR || c === CH_FF || c === CH_VT;
}

/** Ends a bare atom. */
function isDelimiter(c: number): boolean {
  return isWhitespace(c) || c === CH_OPEN || c === CH_CLOSE || c === CH_QUOTE;
}

/**
 * Called once per token. `value` is the decoded text (empty for parens) and
 * `quoted` records whether the source form was a double-quoted string.
 */
export type TokenSink = (
  kind: TokenKind,
  value: string,
  quoted: boolean,
  offset: number,
  line: number,
  column: number,
) => void;

/**
 * Push-based scan. The parser consumes this directly so that a 42 MB board does
 * not have to materialise ~8 million intermediate token objects on the way to
 * the tree. {@link tokenize} is the array-collecting wrapper — one scanner, so
 * the two cannot drift apart.
 */
export function scan(text: string, fileName: string, emit: TokenSink): void {
  let i = 0;
  let line = 1;
  let lineStart = 0;
  const n = text.length;

  const pos = (offset: number) => ({ offset, line, column: offset - lineStart + 1 });

  while (i < n) {
    const c = text.charCodeAt(i);

    if (c === CH_LF) {
      i++;
      line++;
      lineStart = i;
      continue;
    }
    if (isWhitespace(c)) {
      i++;
      continue;
    }

    if (c === CH_OPEN) {
      emit('open', '', false, i, line, i - lineStart + 1);
      i++;
      continue;
    }
    if (c === CH_CLOSE) {
      emit('close', '', false, i, line, i - lineStart + 1);
      i++;
      continue;
    }

    if (c === CH_QUOTE) {
      const startOffset = i;
      const startLine = line;
      const startColumn = i - lineStart + 1;
      i++;

      // Fast path: scan for the closing quote. Most KiCad strings contain no
      // escapes at all, so they can be taken as a single slice.
      const contentStart = i;
      let sawEscape = false;
      let closed = false;
      while (i < n) {
        const ch = text.charCodeAt(i);
        if (ch === CH_BACKSLASH) {
          sawEscape = true;
          if (i + 1 >= n) {
            throw new SexpError('unterminated escape sequence', pos(i), fileName);
          }
          if (text.charCodeAt(i + 1) === CH_LF) {
            line++;
            lineStart = i + 2;
          }
          i += 2;
          continue;
        }
        if (ch === CH_QUOTE) {
          closed = true;
          break;
        }
        if (ch === CH_LF) {
          line++;
          lineStart = i + 1;
        }
        i++;
      }
      if (!closed) {
        throw new SexpError('unterminated string', { offset: startOffset, line: startLine, column: startColumn }, fileName);
      }
      const raw = text.slice(contentStart, i);
      i++; // past the closing quote
      emit('string', sawEscape ? unescapeString(raw) : raw, true, startOffset, startLine, startColumn);
      continue;
    }

    // Bare atom: everything up to whitespace, a paren or a quote.
    const startOffset = i;
    const startColumn = i - lineStart + 1;
    i++;
    while (i < n && !isDelimiter(text.charCodeAt(i))) i++;
    emit('atom', text.slice(startOffset, i), false, startOffset, line, startColumn);
  }
}

/** Collect every token into an array. Convenience over {@link scan}. */
export function tokenize(text: string, fileName = '<input>'): Token[] {
  const tokens: Token[] = [];
  scan(text, fileName, (kind, value, _quoted, offset, line, column) => {
    tokens.push({ kind, value, offset, line, column });
  });
  return tokens;
}

/** Resolve `\"`, `\\`, `\n`, `\t`, `\r`. Unknown escapes are kept verbatim. */
function unescapeString(raw: string): string {
  let out = '';
  let i = 0;
  for (;;) {
    const next = raw.indexOf('\\', i);
    if (next < 0) return out + raw.slice(i);
    out += raw.slice(i, next);
    const esc = raw[next + 1];
    switch (esc) {
      case 'n':
        out += '\n';
        break;
      case 't':
        out += '\t';
        break;
      case 'r':
        out += '\r';
        break;
      case '"':
        out += '"';
        break;
      case '\\':
        out += '\\';
        break;
      case undefined:
        // Trailing backslash: keep it rather than lose a character.
        return out + '\\';
      default:
        // Unknown escape: keep both characters so nothing is lost.
        out += '\\' + esc;
        break;
    }
    i = next + 2;
  }
}
